import type { CSSProperties } from 'react';
import { Viewport } from '@/theme/breakpoints';
import type { Viewport as ViewportValue } from '@/theme/breakpoints';
import { zIndex } from '@/theme/zIndex';
import {
  DESKTOP_BACK_BUTTON_INSET,
  DESKTOP_CONTAINER_MARGIN,
  DESKTOP_FLOATING_FOOTER_BOTTOM,
  DESKTOP_SECTION_SPACING,
  MOBILE_BACK_BUTTON_INSET,
  MOBILE_CONTAINER_MARGIN,
  MOBILE_CONTAINER_PADDING,
  MOBILE_FLOATING_FOOTER_BOTTOM,
  MOBILE_SECTION_SPACING,
} from './Cstretchmodal.constants';
import type {
  CstretchmodalStyleModel,
  CstretchmodalViewportModel,
} from './Cstretchmodal.types';

enum CstretchmodalResponsiveMode {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

const OVERLAY_BASE_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'stretch',
  pointerEvents: 'auto',
  cursor: 'pointer',
};

const CONTAINER_STYLE: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'stretch',
  cursor: 'default',
};

const MODAL_BASE_STYLE: CSSProperties = {
  display: 'flex',
  flex: 1,
  width: '100%',
};

const SURFACE_BASE_STYLE: CSSProperties = {
  flex: 1,
  position: 'relative',
};

// Sibling members of the style model share one key set per CSS family, so the
// `flex` shorthand and per-side `padding*` longhands are used throughout.
const HEADER_BASE_STYLE: CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const CONTENT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
};

const FOOTER_BASE_STYLE: CSSProperties = {
  flex: '0 0 auto',
};

const FLOATING_FOOTER_BASE_STYLE: CSSProperties = {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
  pointerEvents: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  paddingTop: 6,
  paddingRight: 10,
  paddingBottom: 6,
  paddingLeft: 10,
  borderRadius: 999,
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
  border: '1px solid rgba(255,255,255,0.08)',
  backdropFilter: 'blur(12px) saturate(140%)',
  WebkitBackdropFilter: 'blur(12px) saturate(140%)',
  boxShadow: '0 12px 30px -10px rgba(0,0,0,0.6)',
};

const BACK_BUTTON_BASE_STYLE: CSSProperties = {
  position: 'absolute',
  zIndex: zIndex.modalBackButton,
};

const OVERLAY_PORTAL_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
};

interface CstretchmodalViewportModelInput {
  compactAsMobile: boolean;
  padding: number | string;
  viewport?: ViewportValue | null;
}

interface CstretchmodalStyleModelInput {
  contentContainerStyle?: CSSProperties;
  contentZIndex: number;
  modalWrapperStyle?: CSSProperties;
  style?: CSSProperties;
  viewportModel: CstretchmodalViewportModel;
}

interface CstretchmodalDebugStateInput {
  canClose: boolean;
  compactAsMobile: boolean;
  debugContext?: Record<string, unknown>;
  debugName?: string;
  isCompact: boolean;
  isMobile: boolean;
  isTop: boolean;
  overlayPortalTargetId?: string;
  showBackButton: boolean;
  useMobileStyling: boolean;
  viewport?: ViewportValue | null;
  visible: boolean;
}

function getCstretchmodalResponsiveMode({
  compactAsMobile,
  viewport,
}: {
  compactAsMobile: boolean;
  viewport?: ViewportValue | null;
}): CstretchmodalResponsiveMode {
  if (viewport === Viewport.Mobile) {
    return CstretchmodalResponsiveMode.Mobile;
  }

  if (viewport === Viewport.Compact) {
    return compactAsMobile
      ? CstretchmodalResponsiveMode.Mobile
      : CstretchmodalResponsiveMode.Desktop;
  }

  return CstretchmodalResponsiveMode.Desktop;
}

export function buildCstretchmodalViewportModel({
  compactAsMobile,
  padding,
  viewport,
}: CstretchmodalViewportModelInput): CstretchmodalViewportModel {
  const responsiveMode = getCstretchmodalResponsiveMode({ compactAsMobile, viewport });
  const isMobile = viewport === Viewport.Mobile;
  const isCompact = viewport === Viewport.Compact;
  const useMobileStyling = responsiveMode === CstretchmodalResponsiveMode.Mobile;

  return {
    isCompact,
    isMobile,
    useMobileStyling,
    containerMargin: useMobileStyling ? MOBILE_CONTAINER_MARGIN : DESKTOP_CONTAINER_MARGIN,
    containerPadding: useMobileStyling ? MOBILE_CONTAINER_PADDING : padding,
  };
}

export function buildCstretchmodalDebugState({
  canClose,
  compactAsMobile,
  debugContext,
  debugName,
  isCompact,
  isMobile,
  isTop,
  overlayPortalTargetId,
  showBackButton,
  useMobileStyling,
  viewport,
  visible,
}: CstretchmodalDebugStateInput): Record<string, unknown> {
  return {
    debugName: debugName ?? null,
    visible,
    canClose,
    isTop,
    viewport: viewport ?? null,
    isMobile,
    isCompact,
    compactAsMobile,
    useMobileStyling,
    hasOverlayPortalTarget: Boolean(overlayPortalTargetId),
    showBackButton,
    debugContext: debugContext ?? null,
  };
}

export function buildCstretchmodalStyleModel({
  contentContainerStyle,
  contentZIndex,
  modalWrapperStyle,
  style,
  viewportModel,
}: CstretchmodalStyleModelInput): CstretchmodalStyleModel {
  const sectionSpacing = viewportModel.useMobileStyling
    ? MOBILE_SECTION_SPACING
    : DESKTOP_SECTION_SPACING;
  const floatingFooterBottom = viewportModel.useMobileStyling
    ? MOBILE_FLOATING_FOOTER_BOTTOM
    : DESKTOP_FLOATING_FOOTER_BOTTOM;
  const backButtonInset = viewportModel.useMobileStyling
    ? MOBILE_BACK_BUTTON_INSET
    : DESKTOP_BACK_BUTTON_INSET;

  return {
    overlayStyle: {
      ...OVERLAY_BASE_STYLE,
      zIndex: contentZIndex,
    },
    containerStyle: CONTAINER_STYLE,
    containerPadding: viewportModel.containerPadding,
    modalStyle: {
      ...MODAL_BASE_STYLE,
      ...modalWrapperStyle,
    },
    surfaceStyle: {
      margin: viewportModel.containerMargin,
      ...SURFACE_BASE_STYLE,
      ...style,
      ...contentContainerStyle,
    },
    headerStyle: {
      ...HEADER_BASE_STYLE,
      paddingBottom: sectionSpacing,
    },
    contentStyle: CONTENT_STYLE,
    footerStyle: {
      ...FOOTER_BASE_STYLE,
      paddingTop: sectionSpacing,
    },
    floatingFooterStyle: {
      ...FLOATING_FOOTER_BASE_STYLE,
      bottom: floatingFooterBottom,
    },
    backButtonStyle: {
      ...BACK_BUTTON_BASE_STYLE,
      top: backButtonInset,
      right: backButtonInset,
    },
    overlayPortalStyle: OVERLAY_PORTAL_STYLE,
  };
}
